import { CalendarClock, Settings } from 'lucide-react';
import type { BillingPeriod, UserAgentInstance } from '../../../types/agent';
import { billingPeriodLabel } from './publicAgentHelpers';

type Props = {
  open: boolean;
  instance: UserAgentInstance;
  renewing: boolean;
  onRenew: () => void | Promise<void>;
  onOpenSettings: () => void;
  embedInPanel?: boolean;
};

export function AgentPlanExpiredModal({ open, instance, renewing, onRenew, onOpenSettings, embedInPanel = false }: Props) {
  if (!open) return null;

  const credits = Math.max(0, Number(instance.credits_per_period ?? 0));
  const layer = embedInPanel ? 'absolute' : 'fixed';

  return (
    <div
      className={`${layer} inset-0 z-[55] flex items-center justify-center bg-black/65 p-4 sm:p-6`}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="agent-plan-expired-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-subtle/80 bg-elevated/95 p-6 shadow-2xl shadow-black/40 backdrop-blur-md">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-status-warning/35 bg-status-warning/15">
            <CalendarClock className="h-7 w-7 text-status-warning" aria-hidden />
          </div>
          <h2 id="agent-plan-expired-title" className="text-lg font-semibold text-th-primary">
            Plan access has expired
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-th-secondary">
            Your {billingPeriodLabel(instance.selected_billing_period as BillingPeriod)} access
            ended. Renew to keep chatting, using voice, and files for this agent.
          </p>
          {credits > 0 ? (
            <p className="mt-3 rounded-lg border border-subtle/60 bg-surface/80 px-3 py-2 text-sm text-th-primary">
              <span className="text-th-secondary">Renewal cost: </span>
              <span className="font-mono font-semibold tabular-nums">{credits.toLocaleString()}</span>
              <span className="text-th-secondary"> credits</span>
            </p>
          ) : null}
        </div>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            className="button-primary w-full sm:w-auto sm:min-w-[140px]"
            disabled={renewing}
            onClick={() => void onRenew()}
          >
            {renewing ? 'Renewing…' : 'Renew'}
          </button>
          <button
            type="button"
            className="button-secondary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
            onClick={onOpenSettings}
          >
            <Settings className="h-4 w-4 shrink-0" aria-hidden />
            Settings
          </button>
        </div>
      </div>
    </div>
  );
}
